import { useState } from 'react'
import type { ReactElement } from 'react'
import { ACTAS_ROSTER } from '../data/roster-config'
import { BlockPicker } from '../components/BlockPicker'
import type { BlockCode, WeekStart } from '../engine/types'
import { signOut } from '../lib/auth'
import { updateProfile } from '../lib/profile'
import type { RosterType } from '../lib/profile'
import { useSession } from '../lib/session-context'
import { Button, SegmentedControl, SelectField, TextField } from '../ui'
import type { SegmentedOption } from '../ui'

/** The eight blocks, in the roster's own order — matches `BlockPicker`'s options. */
const BLOCK_OPTIONS = ACTAS_ROSTER.blockOrder.map((code) => ({ value: code, label: code }))

/**
 * Labels match `SetupScreen`'s control: one value, one word everywhere
 * downstream — the 0010 guard's error message, the plan and the admin UI all
 * say FWA, so the third label does too ("flexible" in parentheses, since the
 * acronym alone is not self-explaining). Same for `Partial block roster`,
 * which Setup has always called it.
 */
const ROSTER_TYPE_OPTIONS: SegmentedOption<RosterType>[] = [
  { value: 'BLOCK', label: 'Block' },
  { value: 'BLOCK_PARTIAL', label: 'Partial block roster' },
  { value: 'FWA', label: 'FWA (flexible)' },
]

/** Both conventions are genuinely in use in Australia — CLAUDE.md, Task 11's brief. */
const WEEK_START_OPTIONS: Array<{ value: WeekStart; label: string }> = [
  { value: 'SUNDAY', label: 'Sunday' },
  { value: 'MONDAY', label: 'Monday' },
]

/**
 * `Settings` tab, rendered inside `AppShell`'s own tab panel — `AppShell` already wraps its
 * whole tree in one `<AppRoot>`, so this screen deliberately does NOT wrap itself in another
 * (the "doubled Station Ledger chrome" bug Task 14 already found and fixed once this phase).
 * Unlike `SetupScreen`/`AdminScreen` this is not a top-level route.
 *
 * Takes no props — calls `useSession()` itself for `profile` and `session`, same as
 * `SetupScreen`/`AdminScreen`. Only ever rendered once `profile` is non-null (part of `AppShell`,
 * which only renders for the `READY` gate state) — trusted the same way `SetupScreen`'s review
 * already approved, rather than adding a defensive null branch this screen can't usefully recover
 * from anyway.
 *
 * Three save-timing patterns, deliberately different:
 *  - The four text fields (display name, Messenger username, classification, station) save
 *    together behind one "Save changes" button — a text field is the one control here where a
 *    save on every keystroke/blur would mean excessive API calls.
 *  - Block and week start are discrete, deliberate choices with no such cost, so they save
 *    immediately on change, matching Phase 1's existing behaviour for these two controls exactly.
 *  - The roster type is a deliberate *switch*, not an immediate save (Phase 3 plan §1, Oscar's
 *    ruling of 17/09/2026): choosing a different type opens an inline confirmation that states
 *    the consequences in sentences, and only confirming saves — always `roster_type` and
 *    `block_code` together in one `updateProfile` call, so the profile is never half-switched.
 *    A half-switched profile is not just untidy: `isProfileComplete` (`../lib/gate`) requires a
 *    block whenever `roster_type` is `BLOCK`/`BLOCK_PARTIAL`, so saving the type without the
 *    block would flip `decideGate` to 'SETUP' and swap the whole shell out for the setup wizard
 *    mid-Settings. To `BLOCK`/`BLOCK_PARTIAL` the confirm stays disabled until a block is picked,
 *    saying why; to `FWA` it always sends `block_code: null`, because `decideGate` requires a
 *    block only for the block types, so the cleared state is consistent. No switch ever deletes
 *    exceptions or shifts — the merge algebra (`effectiveRoster`) renders whatever survives
 *    deterministically, and the confirmation says what stays.
 */
export function SettingsScreen(): ReactElement {
  const { session, profile, refresh } = useSession()
  // See the docstring above: `SettingsScreen` only ever renders once `AppShell` has already
  // gated on `profile !== null` (the `READY` state). Trusting that precondition, same as
  // `SetupScreen`.
  const activeProfile = profile as NonNullable<typeof profile>

  // --- Text fields, saved together ----------------------------------------
  const [displayName, setDisplayName] = useState(() => activeProfile.display_name ?? '')
  const [facebookUsername, setFacebookUsername] = useState(
    () => activeProfile.facebook_username ?? '',
  )
  const [classification, setClassification] = useState(() => activeProfile.classification ?? '')
  const [station, setStation] = useState(() => activeProfile.station ?? '')
  const [savingDetails, setSavingDetails] = useState(false)
  const [detailsSaved, setDetailsSaved] = useState(false)
  const [detailsError, setDetailsError] = useState<string | null>(null)

  async function handleSaveDetails() {
    setSavingDetails(true)
    setDetailsSaved(false)
    setDetailsError(null)

    const { error } = await updateProfile(activeProfile.id, {
      display_name: displayName.trim() || null,
      facebook_username: facebookUsername.trim() || null,
      classification: classification.trim() || null,
      station: station.trim() || null,
    })

    setSavingDetails(false)
    if (error) {
      setDetailsError(error.message)
      return
    }
    setDetailsSaved(true)
    await refresh()
  }

  // --- Block, saves immediately on change ---------------------------------
  const [blockSaved, setBlockSaved] = useState(false)
  const [blockError, setBlockError] = useState<string | null>(null)

  async function handleBlockChange(value: string) {
    setBlockSaved(false)
    setBlockError(null)
    const { error } = await updateProfile(activeProfile.id, {
      block_code: value as (typeof BLOCK_OPTIONS)[number]['value'],
    })
    if (error) {
      setBlockError(error.message)
      return
    }
    setBlockSaved(true)
    await refresh()
  }

  // --- Roster type: the deliberate switch ----------------------------------
  // `pendingType` is the type the user has chosen but not yet confirmed — the SegmentedControl
  // itself keeps showing the *current* profile type until the switch saves, so the control always
  // answers "what am I now", never "what am I about to be".
  const [pendingType, setPendingType] = useState<RosterType | null>(null)
  const [pendingBlock, setPendingBlock] = useState<BlockCode | null>(null)
  const [switchSaving, setSwitchSaving] = useState(false)
  const [switchSaved, setSwitchSaved] = useState(false)
  const [switchError, setSwitchError] = useState<string | null>(null)

  function handleRosterTypeChange(value: RosterType) {
    if (value === activeProfile.roster_type) {
      // Re-selecting the type you already are is a way back out of a pending switch.
      cancelSwitch()
      return
    }
    setSwitchSaved(false)
    setSwitchError(null)
    setPendingBlock(null)
    setPendingType(value)
  }

  function cancelSwitch() {
    setPendingType(null)
    setPendingBlock(null)
  }

  async function handleSwitchConfirm() {
    if (pendingType === null) return
    // Unreachable through the UI (the confirm is disabled until a block is picked), kept as the
    // guard that makes the non-null `pendingBlock` below honest.
    if (pendingType !== 'FWA' && pendingBlock === null) return

    setSwitchSaving(true)
    setSwitchSaved(false)
    setSwitchError(null)

    // Both fields, one call — never a half-switched profile (see the docstring).
    const { error } = await updateProfile(activeProfile.id, {
      roster_type: pendingType,
      block_code: pendingType === 'FWA' ? null : pendingBlock,
    })

    setSwitchSaving(false)
    if (error) {
      setSwitchError(error.message)
      return
    }
    cancelSwitch()
    setSwitchSaved(true)
    await refresh()
  }

  const pendingSwitchLabel =
    pendingType === 'FWA'
      ? 'Switch to FWA'
      : pendingType === 'BLOCK'
        ? 'Switch to Block'
        : 'Switch to Partial block roster'

  // --- Week start, saves immediately on change ----------------------------
  const [weekStartSaved, setWeekStartSaved] = useState(false)
  const [weekStartError, setWeekStartError] = useState<string | null>(null)

  async function handleWeekStartChange(value: WeekStart) {
    setWeekStartSaved(false)
    setWeekStartError(null)
    const { error } = await updateProfile(activeProfile.id, { week_start: value })
    if (error) {
      setWeekStartError(error.message)
      return
    }
    setWeekStartSaved(true)
    await refresh()
  }

  // --- Sign out ------------------------------------------------------------
  const [signingOut, setSigningOut] = useState(false)
  const [signOutError, setSignOutError] = useState<string | null>(null)

  async function handleSignOut() {
    setSigningOut(true)
    setSignOutError(null)
    const { error } = await signOut()
    // No navigation or manual state update needed on success — SessionProvider's own
    // onAuthStateChange subscription picks up the cleared session, AppRoutes re-evaluates
    // decideGate on its own next render and redirects to /sign-in.
    if (error) {
      setSignOutError(error.message)
      setSigningOut(false)
    }
  }

  // The existing block `SelectField` stays for BLOCK/BLOCK_PARTIAL users changing block *within*
  // their type — the confirmation flow above is only for changing the type itself.
  const showBlockField =
    activeProfile.roster_type === 'BLOCK' || activeProfile.roster_type === 'BLOCK_PARTIAL'

  return (
    <div className="sl-stack">
      <section className="sl-stack">
        <h2 className="sl-heading">Sign-in</h2>
        <p className="sl-hint">
          You sign in with a 6-digit code sent to {session?.user.email ?? 'your email'}.
        </p>
      </section>

      <section className="sl-stack">
        <h2 className="sl-heading">Your details</h2>
        <TextField label="Display name" value={displayName} onChange={setDisplayName} />
        <TextField
          label="Messenger username"
          value={facebookUsername}
          onChange={setFacebookUsername}
          hint="Find yours at facebook.com/your.name, or in your profile's URL."
        />
        <TextField label="Classification" value={classification} onChange={setClassification} />
        <TextField label="Station" value={station} onChange={setStation} />
        {/* Empty display name is blocked here, not merely warned about: `isProfileComplete`
            (`../lib/gate`) requires a non-null `display_name`, so saving one away flips
            `decideGate` to 'SETUP' and swaps the whole shell out for `SetupScreen` mid-edit,
            with no explanation. `SetupScreen` guards its own Continue button the same way. */}
        <Button
          variant="primary"
          disabled={savingDetails || displayName.trim() === ''}
          onClick={() => void handleSaveDetails()}
        >
          Save changes
        </Button>
        {detailsSaved ? (
          <p className="sl-hint" role="status">
            Saved.
          </p>
        ) : null}
        {detailsError ? (
          <p className="sl-hint" role="alert">
            {detailsError}
          </p>
        ) : null}
      </section>

      <section className="sl-stack">
        <SegmentedControl
          label="Roster type"
          options={ROSTER_TYPE_OPTIONS}
          value={activeProfile.roster_type}
          onChange={handleRosterTypeChange}
          hint="Changing this asks first, and never deletes anything you've entered."
        />
        {pendingType !== null ? (
          <div className="sl-stack">
            {pendingType === 'FWA' ? (
              <p className="sl-hint">
                Your block will be cleared; your roster will show only shifts you enter.
              </p>
            ) : (
              <>
                <BlockPicker
                  label="Your new block"
                  value={pendingBlock}
                  onChange={setPendingBlock}
                />
                <p className="sl-hint">
                  Shifts you entered will stay on top of your block roster — delete any you no
                  longer work.
                </p>
                {pendingBlock === null ? (
                  <p className="sl-hint">
                    Pick a block first — a block roster is defined by its block, and saving
                    without one would send you back to setup.
                  </p>
                ) : null}
              </>
            )}
            <Button
              variant="primary"
              disabled={switchSaving || (pendingType !== 'FWA' && pendingBlock === null)}
              onClick={() => void handleSwitchConfirm()}
            >
              {pendingSwitchLabel}
            </Button>
            <Button variant="secondary" disabled={switchSaving} onClick={cancelSwitch}>
              Cancel
            </Button>
          </div>
        ) : null}
        {switchSaved ? (
          <p className="sl-hint" role="status">
            Saved.
          </p>
        ) : null}
        {switchError ? (
          <p className="sl-hint" role="alert">
            {switchError}
          </p>
        ) : null}
      </section>

      {showBlockField ? (
        <section className="sl-stack">
          <SelectField
            label="Your roster block"
            options={BLOCK_OPTIONS}
            value={activeProfile.block_code ?? ''}
            onChange={(value) => void handleBlockChange(value)}
          />
          {blockSaved ? (
            <p className="sl-hint" role="status">
              Saved.
            </p>
          ) : null}
          {blockError ? (
            <p className="sl-hint" role="alert">
              {blockError}
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="sl-stack">
        <SegmentedControl
          label="Week starts on"
          options={WEEK_START_OPTIONS}
          value={activeProfile.week_start}
          onChange={(value) => void handleWeekStartChange(value)}
        />
        {weekStartSaved ? (
          <p className="sl-hint" role="status">
            Saved.
          </p>
        ) : null}
        {weekStartError ? (
          <p className="sl-hint" role="alert">
            {weekStartError}
          </p>
        ) : null}
      </section>

      <section className="sl-stack">
        <Button variant="secondary" disabled={signingOut} onClick={() => void handleSignOut()}>
          Sign out
        </Button>
        {signOutError ? (
          <p className="sl-hint" role="alert">
            {signOutError}
          </p>
        ) : null}
      </section>
    </div>
  )
}
